import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { Activity, Eye, EyeOff, AlertCircle, Sun, Moon } from 'lucide-react';
import './LoginPage.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function LoginPage() {
  const { login, signup, loginWithGoogle } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('coach@matchmind.ai');
  const [password, setPassword] = useState('demo1234');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const googleBtnRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = mode === 'login' ? await login(email, password) : await signup(email, password, name);
    setLoading(false);
    if (result.success) navigate('/dashboard');
    else setError(result.error);
  };

  const handleGoogleCredential = useCallback(async (response) => {
    setError('');
    const result = await loginWithGoogle(response.credential);
    if (result.success) navigate('/dashboard');
    else setError(result.error);
  }, [loginWithGoogle, navigate]);

  // Renders Google's own button (their iframe can't be re-styled, so we
  // theme it to match dark/light mode instead of using a custom-styled one —
  // Google requires clicks to originate from their real button).
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (!window.google?.accounts?.id || !googleBtnRef.current) {
        setTimeout(tryRender, 150);
        return;
      }
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
      });
      googleBtnRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: theme === 'dark' ? 'filled_black' : 'outline',
        size: 'large',
        shape: 'pill',
        width: 368,
        text: 'signin_with',
      });
    };
    tryRender();
    return () => { cancelled = true; };
  }, [theme, handleGoogleCredential]);

  const switchMode = () => {
    setMode(m => (m === 'login' ? 'signup' : 'login'));
    setError('');
  };

  return (
    <div className="login-page">
      <div className="login-bg">
        <div className="login-bg__grid" />
        <div className="login-bg__glow login-bg__glow--1" />
        <div className="login-bg__glow login-bg__glow--2" />
      </div>

      <button
        className="theme-toggle-fab"
        onClick={toggleTheme}
        title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-label="Toggle color theme"
      >
        {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      <div className="login-container">
        <div className="login-card fade-in">
          {/* Logo */}
          <div className="login-logo">
            <div className="login-logo__icon"><Activity size={22} /></div>
            <div>
              <div className="login-logo__name">MatchMind <span>AI</span></div>
              <div className="login-logo__sub">Football Intelligence Platform</div>
            </div>
          </div>

          <div className="login-card__header">
            <h1>{mode === 'login' ? 'Welcome back' : 'Create your account'}</h1>
            <p>{mode === 'login' ? 'Sign in to access your tactical intelligence dashboard' : 'Get your own account — no credit card, no catch'}</p>
          </div>

          {mode === 'login' && (
            <div className="login-demo-hint">
              <span className="badge badge-blue">Demo</span>
              <span>Pre-filled with a real seeded demo account</span>
            </div>
          )}

          {error && (
            <div className="login-error">
              <AlertCircle size={15} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            {mode === 'signup' && (
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your name"
                />
              </div>
            )}
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="coach@club.com"
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <div className="input-wrap">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  minLength={mode === 'signup' ? 6 : undefined}
                  required
                />
                <button type="button" className="input-eye" onClick={() => setShowPass(s => !s)}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button type="submit" className="btn btn-primary login-submit" disabled={loading}>
              {loading ? <span className="spinner" /> : null}
              {loading ? (mode === 'login' ? 'Signing in...' : 'Creating account...') : (mode === 'login' ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          <div className="login-divider"><span>or continue with</span></div>

          {GOOGLE_CLIENT_ID ? (
            <div className="login-google-btn" ref={googleBtnRef} />
          ) : (
            <div className="login-google-unavailable">Google sign-in isn't configured on this server yet</div>
          )}

          <p className="login-footer login-footer--link" onClick={switchMode}>
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <span>{mode === 'login' ? 'Sign up' : 'Sign in'}</span>
          </p>
        </div>

        <div className="login-features">
          {[
            { icon: '⚡', title: 'AI Tactical Analysis', desc: 'Formation, pressing, and pattern recognition' },
            { icon: '📊', title: 'Performance Metrics', desc: 'Player ratings, heatmaps, and trend graphs' },
            { icon: '🎯', title: 'Coaching Insights', desc: 'Actionable recommendations in minutes' },
          ].map(f => (
            <div key={f.title} className="login-feature-card">
              <span>{f.icon}</span>
              <div>
                <div className="login-feature-card__title">{f.title}</div>
                <div className="login-feature-card__desc">{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
